import { useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import { setSlipHandler, type SlipInput } from '../api/client';
import { Modal } from './ui';
import { partOf } from '../lib/format';

/* eslint-disable @typescript-eslint/no-explicit-any */
interface Ask { message: string; details: any; canSkip: boolean; resolve: (v: SlipInput | 'skip' | null) => void }
const d = (iso: string | null | undefined) => (iso ? DateTime.fromISO(iso).toFormat('ccc d LLL') : '');

/**
 * Production performance: a change that moves a job past its committed date asks for the reason (and whether the
 * customer was told, when the new finish is after their date); the change is then sent again with it.
 */
export function SlipPrompt() {
  const [ask, setAsk] = useState<Ask | null>(null);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [informed, setInformed] = useState(false);
  const [customerDate, setCustomerDate] = useState('');
  useEffect(() => {
    setSlipHandler((message, details, canSkip) => new Promise((resolve) => { setReason(''); setNote(''); setInformed(false); setCustomerDate(''); setAsk({ message, details, canSkip, resolve }); }));
    return () => setSlipHandler(null);
  }, []);
  if (!ask) return null;
  const jobs: any[] = ask.details?.jobs ?? [];
  const reasons: string[] = ask.details?.reasons ?? [];
  const needsInformed = jobs.some((j) => j.needsInformed);
  const noParts = jobs.some((j) => !j.partsEvidence);
  const customer = reason === 'Customer request';
  const ok = !!reason && (customer ? !!customerDate : !needsInformed || informed);
  const close = (v: SlipInput | 'skip' | null) => { ask.resolve(v); setAsk(null); };
  return (
    <Modal title="After the committed date" onClose={() => close(null)} footer={<>
      <button className="btn" onClick={() => close(null)}>Cancel change</button>
      {ask.canSkip && <button className="btn" data-testid="slip-skip" onClick={() => close('skip')}>Leave {jobs.length === 1 ? 'this job' : 'these jobs'} out, apply the rest</button>}
      <button className="btn primary" data-testid="slip-save" disabled={!ok} onClick={() => close({ reason, note: note || null, customerInformed: customer ? true : needsInformed ? informed : informed || null, customerDate: customer ? customerDate : null })}>Make change</button>
    </>}>
      <div data-testid="slip-prompt">
        <table className="tbl dense">
          <thead><tr><th>Job</th><th>Committed</th><th>New finish</th><th>Customer date</th></tr></thead>
          <tbody>{jobs.map((j) => <tr key={j.reference}><td><b title={j.reference}>{partOf(j)}</b></td><td>{d(j.committedDate)}</td><td className="bad-text">{d(j.finish)}</td><td>{d(j.customerDate)}</td></tr>)}</tbody>
        </table>
        <div className="grid-2" style={{ marginTop: 12 }}>
          <label className="field">Reason *<select className="input" aria-label="Reason" value={reason} onChange={(e) => setReason(e.target.value)}>
            <option value="" />{reasons.map((r) => <option key={r} value={r} disabled={r === 'Supplier late' && noParts}>{r}{r === 'Supplier late' && noParts ? ' (no late or undated PO)' : ''}</option>)}
          </select></label>
          {customer
            ? <label className="field">Customer's new date *<input className="input" type="date" aria-label="Customer's new date" value={customerDate} onChange={(e) => setCustomerDate(e.target.value)} /></label>
            : <label className="field">Note<input className="input" aria-label="Note" value={note} onChange={(e) => setNote(e.target.value)} /></label>}
        </div>
        {!customer && <label className="check" style={{ marginTop: 10 }}><input type="checkbox" aria-label="Customer informed" checked={informed} onChange={(e) => setInformed(e.target.checked)} /> Customer informed{needsInformed ? ' *' : ''}</label>}
      </div>
    </Modal>
  );
}
